import paymentMethodsConf from '@/configuration/sources/smartform/paymentMethodsConf.yml'
import { capitalize } from '../../common/util/string'
import { CoBrands } from '@/configuration/sources/Brands.yml'
import { brands as debitBrands } from '@/configuration/sources/DebitCardList.yml'
import {
  brandFormat,
  brandCorrespondence,
  simulator
} from '@/configuration/sources/smartform/applePay.yml'
import ErrorsConfiguration from '@/configuration/sources/ErrorsConfiguration.yml'

export const getApplePayState = () => {
  return {
    applePay: {
      testMode: false,
      forceRealWithTestKeys: false, // Used for test purposes only
      real: {
        ready: false,
        open: false
      },
      simulator: {
        ready: false,
        open: false,
        card: null
      }
    }
  }
}

export const applePayActions = ($locator, app) => {
  return {
    createApplePaySession({ state, dispatch, commit }) {
      const {
        applePay: { testMode }
      } = state
      if (testMode) return

      try {
        const { applePayManager } = $locator
        applePayManager.initialize()
      } catch (e) {
        const applePayErrors = ErrorsConfiguration.applePay
        // Search for the error message in the config to get the code
        let errorCode = Object.entries(applePayErrors).find(([, value]) =>
          e.message.includes(value)
        )?.[0]

        // Must create a new ApplePaySession from a user gesture handler error
        // Display the modal to force the user interaction needed to start ApplePaySession
        if (errorCode === 'CLIENT_516') {
          dispatch('openConfirmationModal', {
            method: 'APPLE_PAY',
            errorCode
          })
        } else {
          // If the message is not found, use the generic error
          if (!errorCode) errorCode = 'CLIENT_517'

          dispatch('error', errorCode)
          throw new Error('Could not start ApplePay session', { cause: e })
        }
      }
    },
    startApplePayPayment({ state, dispatch, commit }) {
      const { applePayManager } = $locator
      const {
        applePay: { testMode }
      } = state

      dispatch('cleanError')
      commit('UPDATE', {
        smartForm: { activeMethod: 'APPLE_PAY', activeMethodMetadata: null }
      })
      if (testMode) {
        return dispatch('openApplePaySimulatorModal')
      }

      applePayManager.start()
      dispatch('applePayReady')
    },
    applePayReady({ dispatch, commit }) {
      commit('UPDATE', { applePay: { real: { ready: true } } })
    },
    async applePayMode({ dispatch, commit, getters, state }) {
      const {
        testKeys,
        applePay: { forceRealWithTestKeys }
      } = state

      if (!testKeys || forceRealWithTestKeys) {
        // Always load the script with prod Keys needed to check ApplePay support
        await $locator.scriptLoader.loadScriptPromise(
          paymentMethodsConf.sources.APPLE_PAY,
          'kr-apple-pay'
        )
        try {
          if (!getters.isApplePayCompatible()) {
            dispatch('removePaymentMethod', 'APPLE_PAY')
            dispatch('removeDnaPaymentMethod', 'APPLE_PAY')
          }
        } catch (e) {
          if (e.name === 'InvalidAccessError') {
            dispatch('error', 'CLIENT_507')
            throw new Error('Could not start ApplePay session', { cause: e })
          } else {
            console.error(e)
          }
        }
      } else {
        commit('UPDATE', {
          applePay: {
            testMode: true
          }
        })
        dispatch('loadApplePaySimulator')
      }
    },
    openApplePaySimulatorModal({ commit }) {
      commit('UPDATE', {
        applePay: {
          simulator: {
            open: true
          }
        }
      })
    },
    closeApplePaySimulatorModal({ commit, getters }) {
      if (!getters.isApplePayOpen) return

      commit('UPDATE', {
        applePay: {
          simulator: {
            open: false
          }
        }
      })
    },
    applePayRealModalOpened({ commit }) {
      commit('UPDATE', {
        applePay: {
          real: {
            open: true
          }
        }
      })
    },
    applePayRealModalClosed({ commit }) {
      commit('UPDATE', {
        applePay: {
          real: {
            open: false
          }
        }
      })
    },
    async loadApplePaySimulator() {
      const module = await import(
        /* webpackChunkName: "kr-simulator-apple-pay" */ '@/simulator/applepay/Loader'
      )
      const loader = module.default
      loader($locator)
    },
    setApplePaySimulatorCard({ commit }, card) {
      commit('UPDATE', {
        applePay: {
          simulator: {
            card
          }
        }
      })
    },
    closeApplePay({ dispatch, getters }) {
      const { isApplePaySimulatorOpen, isExtrasFormVisible } = getters

      if (isApplePaySimulatorOpen) dispatch('closeApplePaySimulatorModal')
      if (isExtrasFormVisible) dispatch('hideExtrasForm')
    },
    resetApplePay({ commit }) {
      commit('UPDATE', {
        applePay: {
          real: {
            open: false
          },
          simulator: {
            open: false,
            card: null
          }
        }
      })
    }
  }
}
export const applePayMutations = {}
export const applePayGetters = {
  isApplePayActive: (state, { isActiveMethod }) => isActiveMethod('APPLE_PAY'),
  /**
   * Display ApplePay Button if browsers meets requirements
   * (iOS 16+ or Apple device + Safari)
   * OR if simulator is ready
   *
   * @returns {boolean}
   * @since KJS-2422
   */
  isApplePayCompatible: () => () => {
    return (
      !!ApplePaySession?.canMakePayments && ApplePaySession.canMakePayments()
    )
  },
  /** The paymentCredentialStatus can be:
   *  'paymentCredentialsAvailable', // ApplePay support and active valid wallet card
   * 'paymentCredentialsUnavailable', // ApplePay support but NO active valid wallet card (ApplePay guidelines say not to show the button in this case)
   * 'paymentCredentialStatusUnknown' // Non safari browsers
   * 'applePayUnsupported' // non safari browser but payment may be impossible. Using ApplePaySession.canMakePayments() to check payment capability
   */
  isNonSafariApplePayCompatible:
    (_state, { isApplePayCompatible }) =>
    () => {
      return new Promise(resolve => {
        if (!isApplePayCompatible()) return false
        ApplePaySession.applePayCapabilities().then(
          ({ paymentCredentialStatus }) => {
            resolve(
              [
                'paymentCredentialStatusUnknown',
                'applePayUnsupported'
              ].includes(paymentCredentialStatus)
            ) // Non safari browsers
          }
        )
      })
    },
  /**
   * If simulator is ready, depends on simulator.open.
   * else it should be based on the real ApplePay modal.
   *
   * @returns {boolean}
   * @since KJS-2422
   */
  isApplePayOpen: ({ applePay: { simulator, real } }) =>
    (simulator.ready && simulator.open) || (real.ready && real.open),
  isApplePaySimulatorOpen: ({ applePay: { simulator } }) => simulator.open,
  isApplePayRealOpen: ({ applePay: { real } }) => real.open,
  /**
   * Returns whether or not the simulator is active and ready.
   *
   * @returns {boolean}
   */
  isApplePaySimulator: ({
    applePay: {
      testMode,
      simulator: { ready }
    }
  }) => testMode && ready,
  /**
   * Gets the test card object set on the simulator.
   *
   * @returns {object}
   */
  applePaySimulatorCard: ({
    applePay: {
      simulator: { card }
    }
  }) => card,
  getApplePayTestPayload: (state, getters) => ({
    methodName: 'https://apple.com/apple-pay',
    ...getters.getApplePayTestPayload
  }),
  getApplePayTestPayload: ({
    applePay: {
      simulator: {
        card: { pan, brand }
      }
    },
    dna
  }) => {
    if (!brand || !pan) throw 'CLIENT_311'

    // LATAM brand variation to generic brand
    let formattedBrand = CoBrands.brands[brand] ?? brand

    let type = debitBrands.includes(formattedBrand) ? 'debit' : 'credit'

    // Specific changes of sub-brands must adapt to applePay supported brands
    formattedBrand = brandCorrespondence[formattedBrand] ?? formattedBrand

    // Adapt brand to the format used by ApplePay or empty string if not supported
    formattedBrand = capitalize(brandFormat[formattedBrand]) ?? ''

    // Contact Info if required
    const contactInfo = {}
    const contactRequired =
      !!dna?.smartForm?.APPLE_PAY?.metadata?.walletShippingAddress
    if (contactRequired) {
      contactInfo.shippingContact = simulator.contactInfo
      contactInfo.billingContact = simulator.contactInfo
      // Payer info
      contactInfo.payerName =
        `${simulator.contactInfo.givenName} ${simulator.contactInfo.familyName}`.trim()
      contactInfo.payerEmail = simulator.contactInfo.emailAddress
      contactInfo.payerPhone = simulator.contactInfo.phoneNumber
    }

    // Token
    const token = simulator.token
    token.paymentData.data = 'PAN_' + pan.replaceAll(' ', '')
    token.paymentMethod.displayName = `${formattedBrand} ${pan.slice(-4)}`
    token.paymentMethod.network = formattedBrand
    token.paymentMethod.type = type

    return {
      token,
      ...contactInfo
    }
  }
}
